/* eslint-disable consistent-return */
import type { CSSProperties, ReactElement, ReactNode } from "react"
import { createElement } from "react"

import { HttpStatusCode } from "axios"
import type { Transition } from "framer-motion"
import ceil from "lodash-es/ceil"
import eq from "lodash-es/eq"
import includes from "lodash-es/includes"
import map from "lodash-es/map"
import times from "lodash-es/times"
import memoizeOne, { type MemoizedFn } from "memoize-one"
import { JSX } from "react/jsx-runtime"

import { type TTranslationKeys } from "$/i18n/i18n"

import type { TScreenBreakpoints } from "@/3514/hooks"
import { buildCssClassName, seconds } from "@/3514/utils"

import {
    ECoachingChatConversationErrorVariant as EErrorVariant,
    type TCoachingChatMatrixFeedbackModalChartDimensionConfig as TMatrixChartDimensionConfig,
    type TCoachingChatMatrixFeedbackModalChartGridRect as TMatrixChatGridRect
} from "../types"

import IntrinsicElements = JSX.IntrinsicElements

const SUMMARY_GENERATION_DURATION_IN_MS: number = seconds(3)

const chatSummaryAllowedHTMLTags: (keyof IntrinsicElements)[] = [
    "div",
    "p",
    "span",
    "strong",
    "b",
    "u",
    "i",
    "ul",
    "ol",
    "li"
]

const chatGuideModalListConfig: { title: keyof TTranslationKeys; content: keyof TTranslationKeys }[] = times(
    4,
    (i: number): { title: keyof TTranslationKeys; content: keyof TTranslationKeys } => ({
        title: `entities.coachingChat.guideModal.list.${i + 1}.title` as keyof TTranslationKeys,
        content: `entities.coachingChat.guideModal.list.${i + 1}.description` as keyof TTranslationKeys
    })
)

const errorBoxTexts: {
    title(variant: EErrorVariant): keyof TTranslationKeys
    description(variant: EErrorVariant): keyof TTranslationKeys
    action(variant: EErrorVariant): keyof TTranslationKeys
} = {
    title: (variant: EErrorVariant): keyof TTranslationKeys => {
        if (eq(variant, EErrorVariant.AiStuck)) return "entities.coachingChat.chat.error.aiStuck.title"
        if (includes<EErrorVariant>([EErrorVariant.ApiFailure, EErrorVariant.Api], variant))
            return "entities.coachingChat.chat.error.api.title"
        if (eq(variant, EErrorVariant.Violation)) return "entities.coachingChat.chat.error.violation.title"
    },
    description: (variant: EErrorVariant): keyof TTranslationKeys => {
        if (eq(variant, EErrorVariant.AiStuck)) return "entities.coachingChat.chat.error.aiStuck.description"
        if (includes<EErrorVariant>([EErrorVariant.ApiFailure, EErrorVariant.Api], variant))
            return "entities.coachingChat.chat.error.api.description"
    },
    action: (variant: EErrorVariant): keyof TTranslationKeys => {
        if (eq(variant, EErrorVariant.AiStuck)) return "entities.coachingChat.chat.error.aiStuck.link"
        if (includes<EErrorVariant>([EErrorVariant.ApiFailure, EErrorVariant.Api], variant))
            return "entities.coachingChat.chat.error.action"
    }
}

const reportFeedbackOpenerTextsMap: {
    [V in "seeds" | "conversation"]: {
        firstPart: keyof TTranslationKeys
        action: keyof TTranslationKeys
        lastPart: keyof TTranslationKeys
    }
} = {
    seeds: {
        firstPart: "entities.coachingChat.chat.banner.formError.violation.messageStart",
        action: "entities.coachingChat.chat.banner.formError.violation.action",
        lastPart: "entities.coachingChat.chat.banner.formError.violation.messageEnd"
    },
    conversation: {
        firstPart: "entities.coachingChat.chat.error.violation.messageStart",
        action: "entities.coachingChat.chat.error.violation.violation.action",
        lastPart: "entities.coachingChat.chat.error.violation.messageEnd"
    }
}

const CHAT_AUTOSCROLL_SEARCH_PARAM_NAME: string = "scrollToChat"
const CHAT_AUTOSCROLL_SEARCH_PARAM_VALUE: string = "1"

const CHAT_LOCKING_HTTP_ERROR_STATUSES: HttpStatusCode[] = [HttpStatusCode.Forbidden, HttpStatusCode.NotFound]
const CHAT_NOT_LOCKING_HTTP_ERROR_STATUSES: HttpStatusCode[] = [HttpStatusCode.InternalServerError]

const chatGuideLegaliseRendererAllowedTags: (keyof JSX.IntrinsicElements)[] = [
    "p",
    "span",
    "strong",
    "a",
    "b",
    "u",
    "i",
    "em",
    "ul",
    "ol",
    "li",
    "br"
]

const matrixFeedbackChartConfig: {
    getDimensions(breakpoints: TScreenBreakpoints): TMatrixChartDimensionConfig
    getSpringTransition(params: { isMotionReduced: boolean }): Transition
    getLabels: MemoizedFn<
        (
            rect: TMatrixChatGridRect,
            gridSize: number
        ) => { containerClassName: string; containerStyle: CSSProperties; labelKey: keyof TTranslationKeys }[]
    >
    getArrows: MemoizedFn<(rect: TMatrixChatGridRect, gridSize: number) => ReactNode[]>
} = {
    getDimensions(breakpoints: TScreenBreakpoints): TMatrixChartDimensionConfig {
        return {
            gridSize: breakpoints.xs || breakpoints.sm ? 75 * 2 : 125 * 2,
            quadrantSize: breakpoints.xs || breakpoints.sm ? 75 : 125,
            cellSize: breakpoints.xs || breakpoints.sm ? 75 / 3 : 125 / 3,
            bulletSize: 14
        }
    },
    getSpringTransition(params: { isMotionReduced: boolean }): Transition {
        return { type: "spring", stiffness: 520, damping: 26, duration: params.isMotionReduced ? 0 : 0.4 }
    },
    getLabels: memoizeOne(function (rect: TMatrixChatGridRect, gridSize: number) {
        const _configs = [
            { left: rect.centerX, top: rect.top - 30, className: "translate-x-[-50%] absolute", key: 1 },
            { left: rect.centerX, top: rect.top + gridSize + 15, className: "translate-x-[-50%] absolute", key: 2 },
            {
                left: rect.left - 15,
                top: rect.centerY,
                className: "translate-x-[-100%] translate-y-[-50%] absolute",
                key: 3
            },
            { left: rect.left + gridSize + 15, top: rect.centerY, className: "translate-y-[-50%] absolute", key: 4 }
        ]

        const _labelKeys: (keyof TTranslationKeys)[] = [
            "entities.coachingChat.chat.matrixFeedback.modal.chart.axisLabel1",
            "entities.coachingChat.chat.matrixFeedback.modal.chart.axisLabel2",
            "entities.coachingChat.chat.matrixFeedback.modal.chart.axisLabel3",
            "entities.coachingChat.chat.matrixFeedback.modal.chart.axisLabel4"
        ]

        return map(_configs, (cfg, i: number) => ({
            containerClassName: cfg.className,
            containerStyle: { left: cfg.left, top: cfg.top },
            labelKey: _labelKeys[i]
        }))
    }),
    getArrows: memoizeOne(function (rect: TMatrixChatGridRect, gridSize: number): ReactNode[] {
        const _configs = [
            {
                left: ceil(rect.centerX),
                top: rect.top,
                className: "translate-x-[-50%] translate-y-[-100%]",
                key: "arrow-t"
            },
            {
                left: ceil(rect.centerX),
                top: rect.top + gridSize,
                className: "translate-x-[-50%] translate-y-0 rotate-180",
                key: "arrow-b"
            },
            {
                left: rect.left,
                top: ceil(rect.centerY),
                className: "translate-x-[-100%] translate-y-[-50%] -rotate-90",
                key: "arrow-l"
            },
            {
                left: rect.left + gridSize,
                top: ceil(rect.centerY),
                className: "translate-y-[-50%] rotate-90",
                key: "arrow-r"
            }
        ]

        return map(
            _configs,
            ({ left, top, className, key }): ReactElement =>
                createElement(
                    "svg",
                    {
                        key,
                        className: buildCssClassName("absolute", className),
                        width: 8,
                        height: 8,
                        viewBox: "0 0 8 8",
                        style: { left, top },
                        "aria-hidden": "true"
                    },
                    createElement("path", {
                        d: "M5 0 L10 10 L0 10 Z",
                        fill: "currentColor",
                        className: "text-gray-23 scale-[0.8]"
                    })
                )
        )
    })
}

const STREAMING_CHUNKS_THRESHOLD_GAP: number = seconds(30)

const config = {
    SUMMARY_GENERATION_DURATION_IN_MS,
    chatSummaryAllowedHTMLTags,
    chatGuideModalListConfig,
    errorBoxTexts,
    reportFeedbackOpenerTextsMap,
    CHAT_AUTOSCROLL_SEARCH_PARAM_NAME,
    CHAT_AUTOSCROLL_SEARCH_PARAM_VALUE,
    CHAT_LOCKING_HTTP_ERROR_STATUSES,
    CHAT_NOT_LOCKING_HTTP_ERROR_STATUSES,
    chatGuideLegaliseRendererAllowedTags,
    matrixFeedbackChartConfig,
    STREAMING_CHUNKS_THRESHOLD_GAP
}

export { config as coachingChatUiConfig }
